import { ReactElement, useMemo, useRef, useState } from 'react'
import { get2DLineOrthoDirection, projectWall2D } from '@editorUtils'
import { wallIsCartesian } from '@geometry'
import {
  DrawControllerWrapper,
  hashObject,
  toVector2,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import { filter, find, isNull, maxBy, reduce, unionBy } from 'lodash-es'
import { Group, Line3 } from 'three'
import { useTheme } from '@mui/material'
import { useModelSnapTargets } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/hooks'
import { WallUpdate } from '@editor/pages/StructuralPlanning/components/WallGeometryForm/utils'
import {
  InteractiveLine,
  InteractiveLineHandles,
  InteractiveLineRef,
  sceneConfig,
  TransientDrawState,
  getCenterMultiple,
  isPointOnLineSegment,
  toImmutable,
  ImmutableVector3,
  useCanvasListener,
} from '@modugen/ui-toolkit'
import { Line, Torus } from '@react-three/drei'
import { useControlStore, useModelStore, useSceneStore } from '@editorStores'
import { useBlockAction, useClickActionEnabled, usePositionGuidToWallGuid } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { roundXYImmutableVector3 } from 'src/geometry/rounding'
import { NUM_DECIMALS_ROUND_WALL_EDIT, WALL_EDIT_IN_PROGRESS_BLOCK } from './constants'
import {
  applyIntelligentTranslation,
  IntelligentConnectedWall,
  findIntelligentConnectedWalls,
  getIntelligentPreviewLine,
} from './intelligentWallEdit'

const openingElevation = 0.1
const wallEndToOpeningThreshold = 0.1

const interactiveLineName = 'interactive-line'

export interface WallEditChange {
  start: ImmutableVector3
  end: ImmutableVector3
  /** connected walls moved along by "Intelligentes Verschieben" (all of them, not just this edit's) */
  dependentWalls: ShapeObject[]
}

interface Props {
  /** the wall as currently edited in the wall form (not the saved wall) */
  wall: ShapeObject
  /** unsaved geometry of connected walls, held by the wall form */
  dependentWalls: ShapeObject[]
  /** an unsaved, freshly drawn wall: it can be moved, but has no handles to extend it */
  isDraft: boolean
  /**
   * what the unsaved edit does so far: the backend accepts either a length change or a translation
   * per save, so after one kind of edit only that kind is offered until the wall is saved
   */
  update: WallUpdate
  onChange: (change: WallEditChange) => void
}

/**
 * Scene part of the wall form in 2D: renders the wall's current form geometry as an editable line
 * and reports moves/extensions through `onChange`. It never touches the model store — the wall form
 * is the only owner of the edit state (see `WallFormScene`).
 */
const WallEdit = ({ wall, dependentWalls, isDraft, update, onChange }: Props): ReactElement => {
  const { scenePalette } = useTheme()

  const lineRef = useRef<InteractiveLineRef>(null)
  const openingsGroupRef = useRef<Group>(null)
  const intelligentConnectedWallsRef = useRef<IntelligentConnectedWall[]>([])
  const intelligentOriginalStartRef = useRef<ImmutableVector3 | null>(null)

  const savedWalls = useModelStore(state => state.model.walls)
  const rips = useModelStore(state => state.model.rips)
  const guidToColor = useSceneStore(state => state.guidToColor)

  const wallGuid = wall.guid

  // the walls as the form currently shows them, so a second move continues from the first one
  const walls = useMemo(
    () => unionBy([wall, ...dependentWalls], savedWalls, 'guid'),
    [wall, dependentWalls, savedWalls],
  )

  const projectedWall = useMemo(() => projectWall2D(wall), [wall])

  const [isMoving, setIsMoving] = useState(false)
  const [activeHandle, setActiveHandle] = useState<
    typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles] | null
  >(null)
  const [intelligentPreviewLines, setIntelligentPreviewLines] = useState<
    [ImmutableVector3, ImmutableVector3][]
  >([])

  // Register a block while a move/extend is actively in progress (handle grabbed or moving). The
  // block outlives the interaction just long enough: the click that finalizes the edit resolves
  // before the `mouseup` that clears this state, so subscribers still see it and refuse to select.
  useBlockAction(
    WALL_EDIT_IN_PROGRESS_BLOCK,
    isMoving || activeHandle !== null ? 'provider' : undefined,
  )

  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const intelligentWallEditEnabled = useControlStore(state => state.intelligentWallEditEnabled)

  const positionGuidToWallGuid = usePositionGuidToWallGuid()

  const ripGuids = useMemo(() => rips.map(rip => rip.position_guid), [rips])

  const guidsToRejectAsSnapTargets = useMemo(() => {
    const filteredRips = filter(ripGuids, guid => positionGuidToWallGuid[guid] === wallGuid)

    return [wallGuid, ...filteredRips]
  }, [wallGuid, ripGuids, positionGuidToWallGuid])

  const modelSnapTargets = useModelSnapTargets({
    xyOnly: true,
    rejectedGuids: guidsToRejectAsSnapTargets,
  })
  const tapelineSnapTargets = useTapelineSnapTargets(true)
  const tapelineCenterTargets = useTapelineCentersSnapTargets(true)
  const additionalSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets, ...tapelineCenterTargets],
    [modelSnapTargets, tapelineSnapTargets, tapelineCenterTargets],
  )

  // Effects
  useDefault2DSnapConfigForElementType(SnapTargetElementType.WALLS)

  // HOTKEYS

  const clearIntelligentPreview = () => {
    intelligentConnectedWallsRef.current = []
    intelligentOriginalStartRef.current = null
    setIntelligentPreviewLines([])
  }

  const updateIntelligentPreviewFromDelta = (delta: ImmutableVector3) => {
    const lines = intelligentConnectedWallsRef.current
      .map(connected => getIntelligentPreviewLine(connected, delta))
      .filter((line): line is [ImmutableVector3, ImmutableVector3] => line !== null)
    setIntelligentPreviewLines(lines)
  }

  useStructuralPlanningDrawerEsc(() => {
    if (activeHandle) {
      setActiveHandle(null)
      lineRef.current?.reset()
    } else if (isMoving) {
      setIsMoving(false)
      lineRef.current?.reset()
      clearIntelligentPreview()
    }
  }, !!activeHandle || isMoving)

  // MEMOS

  const wallCenter = useMemo(() => getCenterMultiple(projectedWall.points), [projectedWall])

  const wallDirection = useMemo(
    () => projectedWall.points[1].sub(projectedWall.points[0]).normalize(),
    [projectedWall],
  )

  const wallOrthoDirection = useMemo(() => {
    const direction2d = get2DLineOrthoDirection(
      toVector2(projectedWall.points[0]),
      toVector2(projectedWall.points[1]),
    )

    return new ImmutableVector3(direction2d.x, direction2d.y)
  }, [projectedWall])

  const drawingOrigin = useMemo(
    () =>
      activeHandle === InteractiveLineHandles.End
        ? projectedWall.points[0]
        : projectedWall.points[1],
    [activeHandle, projectedWall],
  )

  const projectedOpeningPoints = useMemo(
    () =>
      reduce(
        projectedWall.openings,
        (collector, opening) => [...collector, ...opening.points],
        [] as ImmutableVector3[],
      ),
    [projectedWall],
  )

  // EVENTS

  const moveStart = () => {
    if (!isMoving) {
      setIsMoving(true)

      if (intelligentWallEditEnabled && wallIsCartesian(wall)) {
        intelligentConnectedWallsRef.current = findIntelligentConnectedWalls(wall, walls)
        intelligentOriginalStartRef.current = projectedWall.points[0]
      } else {
        clearIntelligentPreview()
      }
    }
  }

  const editStop = (type: 'translation' | 'length') => {
    const wallLine = lineRef.current?.transientLine.current

    if (wallLine) {
      const [drawerStart, drawerEnd] = wallLine
      const start = roundXYImmutableVector3(drawerStart, NUM_DECIMALS_ROUND_WALL_EDIT)
      const end = roundXYImmutableVector3(drawerEnd, NUM_DECIMALS_ROUND_WALL_EDIT)

      const originalStartV = wall.shape.points[0]
      let updatedDependentWalls = dependentWalls

      if (type === 'translation') {
        // the form's preview moves the openings along, so the moved group is reset
        if (openingsGroupRef.current) {
          openingsGroupRef.current.position.set(0, 0, openingElevation)
        }

        if (intelligentConnectedWallsRef.current.length > 0) {
          const delta = new ImmutableVector3(
            start.x - originalStartV.x,
            start.y - originalStartV.y,
            0,
          )
          const updated = intelligentConnectedWallsRef.current
            .map(connected => applyIntelligentTranslation(connected, delta))
            .filter((updated): updated is ShapeObject => updated !== null)

          updatedDependentWalls = unionBy(updated, dependentWalls, 'guid')
        }
      }

      onChange({
        start: new ImmutableVector3(start.x, start.y, wall.shape.points[0].z),
        end: new ImmutableVector3(end.x, end.y, wall.shape.points[1].z),
        dependentWalls: updatedDependentWalls,
      })

      clearIntelligentPreview()
    }
  }

  const stopEditing = () => {
    editStop('length')
    setActiveHandle(null)
  }

  const stopMoving = () => {
    editStop('translation')
    setIsMoving(false)
  }

  useCanvasListener(
    'mouseup',
    event => {
      const isInput = event.target === lineRef.current?.input.current

      if (isMoving) {
        stopMoving()
      }
      if (!isInput && !lineRef.current?.inputActive.current && activeHandle) {
        stopEditing()
      }
    },
    [isMoving, activeHandle, editStop, stopEditing, stopMoving],
  )

  const moveLine = (transientDrawState: TransientDrawState) => {
    const wallLine = lineRef.current?.transientLine.current

    if (transientDrawState.drawPoint && wallLine) {
      const [start, end] = wallLine
      const drawStart2 = getCenterMultiple([start, end])

      const projectedStart = drawStart2.projectOnVector(wallOrthoDirection)
      const projectedEnd = transientDrawState.drawPoint.projectOnVector(wallOrthoDirection)
      const distance = projectedStart.distanceTo(projectedEnd)

      const drawDirection = transientDrawState.drawPoint.sub(drawStart2).normalize()

      lineRef.current?.moveLine(drawDirection, distance)

      if (openingsGroupRef.current) {
        const newPosition = openingsGroupRef.current.position.addScaledVector(
          drawDirection.v,
          distance,
        )
        openingsGroupRef.current?.position.set(newPosition.x, newPosition.y, openingElevation)
      }

      const updatedLine = lineRef.current?.transientLine.current
      const originalStart = intelligentOriginalStartRef.current
      if (originalStart && updatedLine) {
        const [newStart] = updatedLine
        updateIntelligentPreviewFromDelta(
          new ImmutableVector3(newStart.x - originalStart.x, newStart.y - originalStart.y, 0),
        )
      }
    }
  }

  const findDrawPoint = (
    drawingOrigin: ImmutableVector3,
    drawPoint: ImmutableVector3,
    openingPoints: ImmutableVector3[],
  ) => {
    // find all opening points that are not on the line drawn by the user. We
    // want to prevent the user from clipping an opening so he/she should only
    // be able to draw until he encounters an opening
    const openingPointsNotOnLine = filter(
      openingPoints,
      point =>
        !isPointOnLineSegment(
          new Line3(drawingOrigin.v, drawPoint.v),
          point.v,
          wallEndToOpeningThreshold,
        ),
    )

    let finalDrawPoint = drawPoint
    if (openingPointsNotOnLine.length) {
      drawPoint = maxBy(openingPointsNotOnLine, a =>
        a.distanceTo(drawingOrigin),
      ) as ImmutableVector3
    }

    // when the drawpoint is one of the projected opening points we want to set a small
    // threshold so the user is not removing one side of the opening and
    // therefore open the opening (pun intended)
    const openingPointWithingThresholdDistance = find(
      projectedOpeningPoints,
      point => point.distanceTo(drawPoint) < wallEndToOpeningThreshold,
    )
    if (openingPointWithingThresholdDistance) {
      const drawingDirection =
        activeHandle === InteractiveLineHandles.End ? wallDirection.v : wallDirection.v.negate()

      finalDrawPoint = openingPointWithingThresholdDistance.addScaledVector(
        toImmutable(drawingDirection),
        wallEndToOpeningThreshold,
      )
    }

    return finalDrawPoint
  }

  const extendLine = (transientDrawState: TransientDrawState) => {
    if (lineRef.current?.inputActive.current) return

    if (activeHandle && transientDrawState.drawPoint) {
      const drawingEnd = transientDrawState.drawPoint

      const drawPoint = findDrawPoint(drawingOrigin, drawingEnd, projectedOpeningPoints)

      lineRef.current?.updateHandle(activeHandle, drawPoint)
    }
  }

  const onExtendStart = (
    handle: typeof InteractiveLineHandles[keyof typeof InteractiveLineHandles],
  ) => {
    if (isNull(activeHandle)) {
      setActiveHandle(handle)
    }
  }

  // Generate a hash for the wall object

  const zTranslation = 80

  const drawControllerEnabled = useClickActionEnabled()

  // one kind of edit per save (see `update`): after a length change only the moved handle(s),
  // after a translation only further translations
  const isLengthChanged = update.kind === 'length'
  const isTranslated = update.kind === 'translation'
  const isInvalidUpdate = update.kind === 'invalid'
  const changedHandles = [
    ...(update.startChanged ? [InteractiveLineHandles.Start] : []),
    ...(update.endChanged ? [InteractiveLineHandles.End] : []),
  ]

  return (
    <>
      {/* draw controller for moving */}
      <DrawControllerWrapper
        drawingAxis={{
          origin: wallCenter,
          direction: wallOrthoDirection,
        }}
        indicatorType="crosshair"
        enableIndicator={false}
        enabled={isMoving && !activeHandle && drawControllerEnabled}
        onMouseMove={moveLine}
        additionalSnapTargets={additionalSnapTargets}
        snapToAngles={snapToAngles}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        xyOnly
        isValidDrawTarget={object => object.name !== interactiveLineName}
      />

      {/* draw controller for extending */}
      <DrawControllerWrapper
        drawingAxis={{
          origin: wallCenter,
          direction: wallDirection,
        }}
        indicatorType="crosshair"
        enableIndicator={false}
        enabled={!!activeHandle && !isMoving && drawControllerEnabled}
        onMouseMove={extendLine}
        additionalSnapTargets={additionalSnapTargets}
        snapToAngles={snapToAngles}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        xyOnly
      />

      <group position={[0, 0, zTranslation]}>
        <InteractiveLine
          key={hashObject(wall)}
          mainLineProps={{ name: interactiveLineName }}
          boundingLineProps={{ name: interactiveLineName }}
          ref={lineRef}
          line={projectedWall.points as [ImmutableVector3, ImmutableVector3]}
          onClick={moveStart}
          clickDisabled={!!activeHandle || isLengthChanged || isInvalidUpdate}
          showHandles={!isMoving && !isDraft && !isTranslated && !isInvalidUpdate}
          onClickHandle={onExtendStart}
          clickHandleDisabled={isMoving || isTranslated || isInvalidUpdate}
          clickableHandles={
            activeHandle ? [activeHandle] : isLengthChanged ? changedHandles : undefined
          }
          nonSelectable={isMoving || !!activeHandle}
          cursor={isDraft || isLengthChanged || isInvalidUpdate ? 'auto' : 'grab'}
          indicatorCursor={isDraft || isTranslated || isInvalidUpdate ? 'auto' : 'grab'}
          input={undefined}
          handleColor={activeHandle ? 'purple' : undefined}
        />

        {/* Red torus at projectedWall.points[0], indicating start */}
        <Torus
          position={[
            projectedWall.points[0].x,
            projectedWall.points[0].y,
            projectedWall.points[0].z,
          ]}
          args={[0.15, 0.01, 8, 16]}
        >
          <meshBasicMaterial color="red" />
        </Torus>
        {/* Black torus at projectedWall.points[0], indicating start */}
        <Torus
          position={[
            projectedWall.points[1].x,
            projectedWall.points[1].y,
            projectedWall.points[1].z,
          ]}
          args={[0.15, 0.01, 8, 16]}
        >
          <meshBasicMaterial color="black" />
        </Torus>

        <group
          ref={openingsGroupRef}
          position={[0, 0, openingElevation]}
          layers={sceneConfig.R3FNonSelectableObjectLayer}
        >
          {projectedWall.openings?.map((opening, i) => (
            <Line
              key={opening.guid || `${projectedWall.guid}-opening-${i}`}
              points={opening.points.map(p => p.v)}
              color={(guidToColor[opening.guid] || scenePalette.elements2d.openings) as string}
            />
          ))}
        </group>

        {intelligentPreviewLines.length > 0 && (
          <group layers={sceneConfig.R3FNonSelectableObjectLayer}>
            {intelligentPreviewLines.map((line, index) => (
              <Line
                key={`intelligent-preview-${index}`}
                points={[line[0].v, line[1].v]}
                color="purple"
                lineWidth={2}
              />
            ))}
          </group>
        )}
      </group>
    </>
  )
}

export default WallEdit
